'use client';

// PostHogProvider — defers loading the posthog-js SDK until the visitor first
// engages (or 5 s after load; lib/analytics/engagement.ts), so the recorder
// bundle and main posthog-js code stay off the page's first seconds. Until the SDK loads, calls to `capturePostHogEvent()` are queued and
// flushed when init completes. Pageviews captured before idle are queued via
// the same path; subsequent route changes capture directly through window.__posthog.

import { useEffect } from 'react';
import { afterEngagement } from '@/lib/analytics/engagement';
import type { ReactNode } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  capturePostHogEvent,
  flushPostHogQueue,
} from '@/lib/analytics/posthog-capture';

let initStarted = false;

function PostHogInit({ tenantId, tenantSlug }: { tenantId?: string; tenantSlug?: string }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    if (!key || initStarted) return;
    initStarted = true;

    const start = () => {
      void import('posthog-js').then(({ default: posthog }) => {
        posthog.init(key, {
          api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com',
          capture_pageview: false,
          persistence: 'localStorage',
          // Session replays ENABLED for the admin Session Replays page.
          // Loading posthog-recorder.js costs ~52 KB + ~420 ms of main-thread
          // work — mitigated by deferring init to the visitor's first
          // engagement (below), off the page's first seconds.
          disable_session_recording: false,
          // Surveys feature also pulls a separate ~33 KB script most stores
          // never use.
          disable_surveys: true,
          loaded: (ph) => {
            const host = window.location.hostname;
            const slug = tenantSlug || host.split('.')[0] || '';
            const id = tenantId || '';
            ph.register({ tenant_id: id, tenant_slug: slug });
            flushPostHogQueue(ph);
          },
        });
      });
    };

    afterEngagement(start);
  }, [tenantId, tenantSlug]);

  useEffect(() => {
    if (!pathname) return;
    let url = window.location.origin + pathname;
    if (searchParams?.toString()) url += '?' + searchParams.toString();
    capturePostHogEvent('$pageview', { $current_url: url });
  }, [pathname, searchParams]);

  return null;
}

export function PostHogProvider({
  children,
  tenantId,
  tenantSlug,
}: {
  children: ReactNode;
  tenantId?: string;
  tenantSlug?: string;
}) {
  return (
    <>
      <PostHogInit tenantId={tenantId} tenantSlug={tenantSlug} />
      {children}
    </>
  );
}
